import { addDoc, collection, deleteDoc, doc, getDocs, limit, orderBy, query } from 'firebase/firestore'
import {
  COLLECTIONS,
  isWorthReporting,
  problemReportSchema,
  type ProblemReport,
} from '@budgetmonkey/shared'
import { db } from '@/lib/firebase'
import { codeOf } from '@/lib/errorMessage'

/**
 * Telling somebody when a user is stuck.
 *
 * Until this existed, the only monitoring was a person mentioning it. A
 * colleague's uploads were refused for a month — the cause was a project
 * permission, fixed in a minute once anyone knew — and the only reason it ever
 * surfaced was that he thought to send a screenshot. With one other user that
 * is survivable. With six it means the quiet ones simply stop using the app.
 *
 * Three rules keep this from becoming the noise it replaces:
 *
 * - **Only failures that do not fix themselves** (`isWorthReporting`). Offline
 *   is not reported: it is the normal condition of a phone in a building, the
 *   screen says so already, and it heals.
 * - **Once per session per kind.** A dashboard with four failing queries is
 *   one problem, not four, and a retry loop is not forty.
 * - **It never raises.** A reporting failure that broke the page would be a
 *   monitoring tool causing the outage it exists to describe.
 */

/** `code|context` already reported, so a repeat is not written again. */
const reportedThisSession = new Set<string>()

/**
 * Who is signed in, set by the auth provider.
 *
 * Held here rather than passed through every call site because the global
 * error handler that catches most failures sits in the query client, which has
 * no React context to read. Null before sign-in and after sign-out, and a
 * report without an identity is simply not written — the rules would refuse it
 * anyway, since a report must name its own author.
 */
let identity: { userId: string; email: string } | null = null

export function setReportingIdentity(next: { userId: string; email: string } | null): void {
  identity = next
  if (next === null) reportedThisSession.clear()
}

export interface ProblemContext {
  userId: string
  email: string
  /** What the person was doing: "uploading a receipt". */
  context: string
}

// The same helper the screens' messages are built from, deliberately not a
// second copy: two of them mean the code shown to the person and the code
// attached to their report can drift apart, and the report is what gets acted
// on.

/**
 * Record a failure if it is one worth telling an administrator about.
 *
 * Returns whether anything was written, which is for tests — no caller should
 * change behaviour on the strength of it.
 */
export async function reportProblem(
  error: unknown,
  { userId, email, context }: ProblemContext,
): Promise<boolean> {
  const code = codeOf(error)
  if (!isWorthReporting(code)) return false

  const key = `${code}|${context}`
  if (reportedThisSession.has(key)) return false
  // Marked before the write, not after: two failures in the same tick would
  // otherwise both pass the check and write.
  reportedThisSession.add(key)

  const report: ProblemReport = {
    userId,
    email,
    code,
    context,
    at: new Date().toISOString(),
  }

  const parsed = problemReportSchema.safeParse(report)
  if (!parsed.success) return false

  try {
    const { id: _ignored, ...document } = parsed.data
    await addDoc(collection(db, COLLECTIONS.problemReports), document)
    return true
  } catch {
    // Nothing to do here, and nothing worth saying on screen. The person has
    // already been told what failed; this was the attempt to tell someone else.
    reportedThisSession.delete(key)
    return false
  }
}

/**
 * Report a failure for whoever is signed in — the form the global handler uses.
 */
export async function reportProblemForCurrentUser(
  error: unknown,
  context: string,
): Promise<boolean> {
  if (identity === null) return false
  return reportProblem(error, { ...identity, context })
}


/** The most recent problems, for whoever can act on them. */
export async function fetchProblemReports(max = 50): Promise<ProblemReport[]> {
  const snapshot = await getDocs(
    query(collection(db, COLLECTIONS.problemReports), orderBy('at', 'desc'), limit(max)),
  )
  return snapshot.docs.flatMap((document) => {
    const parsed = problemReportSchema.safeParse({ ...document.data(), id: document.id })
    return parsed.success ? [parsed.data] : []
  })
}

export async function clearProblemReports(reports: readonly ProblemReport[]): Promise<void> {
  await Promise.all(
    reports
      .filter((report) => report.id !== undefined)
      .map((report) => deleteDoc(doc(db, COLLECTIONS.problemReports, report.id!))),
  )
}
